import type { ColumnsType } from "@similarweb/pro-logic-components";
import { TextAnchorCell, TextCell } from "@similarweb/pro-logic-components";
import { Type } from "@similarweb/ui-components";

import { TrafficShare } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import { i18nCategoryFilter } from "app/filters/ngFilters";
import { renderTaxonomyPathDisplayNamesCellFactory } from "app/pages/sales-intelligence/common-components/table-cells";
import { CompanyDomainsTooltipCell } from "app/pages/sales-intelligence/common-components/table-cells/components/CompanyDomainsTooltipCell/CompanyDomainsTooltipCell";
import { COMPANY_ANALYSIS_OVERVIEW } from "app/pages/sales-intelligence/constants/routes";
import ColumnsService from "app/pages/sales-intelligence/modules/company-analysis/services/columns/factory";
import type { CompanyAnalysisCompetititorItem } from "app/pages/sales-intelligence/modules/company-analysis/types/competitors";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import { formatTaxonomyPathDisplayNames } from "app/pages/sales-intelligence/utils/taxonomy-tree/path";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { StyledCompanyName, StyledItemIcon } from "./styles";

const COLUMN_COMPANY_NAME = "name";
const COLUMN_INDUSTRY = "category";
const COLUMN_INDUSTRY_TAXONOMY = "industryTaxonomy";
const COLUMN_AFFINITY = "affinity";
const COLUMN_REGIONAL_DOMAINS = "regionalDomains";

const INDUSTRY_COLUMN_WIDTH = 304;

/**
 * The two classifications share one slot: the backend serves whichever the account is entitled to, so
 * a row never carries both (ADR-0013). Sorting the taxonomy goes through the formatted path rather
 * than `ColumnsService.stringSorter`, which only accepts keys whose value is already a string.
 */
const getIndustryColumn = (showsIndustryTaxonomy: boolean): ColumnsType<CompanyAnalysisCompetititorItem>[number] =>
    showsIndustryTaxonomy
        ? {
              dataIndex: COLUMN_INDUSTRY_TAXONOMY,
              title: translate(leadsSearchFiltersI18nKeys.industryTaxonomyName()),
              sorter: (a: CompanyAnalysisCompetititorItem, b: CompanyAnalysisCompetititorItem) =>
                  formatTaxonomyPathDisplayNames(a.industryTaxonomy?.path).localeCompare(
                      formatTaxonomyPathDisplayNames(b.industryTaxonomy?.path),
                  ),
              render: (value: CompanyAnalysisCompetititorItem[typeof COLUMN_INDUSTRY_TAXONOMY]) =>
                  renderTaxonomyPathDisplayNamesCellFactory(value?.path),
              // The cell truncates and tooltips itself; rc-table would otherwise also stamp a native
              // `title`, showing two tooltips on hover.
              ellipsis: { showTitle: false },
              width: INDUSTRY_COLUMN_WIDTH,
          }
        : {
              dataIndex: COLUMN_INDUSTRY,
              sorter: ColumnsService.stringSorter(COLUMN_INDUSTRY),
              title: translate("analysis.competitors.similar.table.columns.category.title"),
              render: (value: CompanyAnalysisCompetititorItem[typeof COLUMN_INDUSTRY]) => {
                  const formatted = value ? i18nCategoryFilter()(value) : "";
                  return <TextCell>{formatted}</TextCell>;
              },
              width: INDUSTRY_COLUMN_WIDTH,
          };

export const getCompetitorsColumns = (
    count: number,
    showsIndustryTaxonomy: boolean,
): ColumnsType<CompanyAnalysisCompetititorItem> => {
    return [
        {
            dataIndex: COLUMN_COMPANY_NAME,
            fixed: "left",
            title: `${translate("analysis.competitors.similar.table.columns.company.title")} ${count ? ` (${count})` : ""}`,
            sorter: ColumnsService.stringSorter(COLUMN_COMPANY_NAME),

            render: (
                value: CompanyAnalysisCompetititorItem[typeof COLUMN_COMPANY_NAME],
                row: CompanyAnalysisCompetititorItem,
            ) => (
                <TextAnchorCell
                    href={proNavigator.href(COMPANY_ANALYSIS_OVERVIEW, {
                        key: row.domain,
                    })}
                >
                    <StyledCompanyName>
                        <StyledItemIcon
                            iconType={Type.Website}
                            iconSrc={row.favicon || ""}
                        />
                        <span>{value}</span>
                    </StyledCompanyName>
                </TextAnchorCell>
            ),
            width: 304,
        },
        getIndustryColumn(showsIndustryTaxonomy),
        {
            dataIndex: COLUMN_REGIONAL_DOMAINS,
            title: translate("analysis.competitors.similar.table.columns.company-domains.title"),
            render: (
                value: CompanyAnalysisCompetititorItem[typeof COLUMN_REGIONAL_DOMAINS],
                row: CompanyAnalysisCompetititorItem,
            ) => (
                <CompanyDomainsTooltipCell
                    value={[{ regionalDomain: row.domain, countryCode: row.country }, ...value]}
                />
            ),
            width: 180,
        },
        {
            dataIndex: COLUMN_AFFINITY,
            sorter: ColumnsService.numberSorter(COLUMN_AFFINITY),
            title: translate("analysis.competitors.similar.table.columns.share.title"),
            render: (
                value: CompanyAnalysisCompetititorItem[typeof COLUMN_AFFINITY],
                row: CompanyAnalysisCompetititorItem,
            ) => (
                <TrafficShare
                    value={value}
                    row={row}
                />
            ),
            width: 180,
        },
    ];
};
